import React, { useState } from 'react'
import { Drawer } from '@mui/material'
import { datadogRum } from '@datadog/browser-rum'
import Cart from './Cart'
import TopUpSection from './TopUp'
import { CanteenCartItem } from './types'
import { FragmentType, graphql, useFragment } from '@/generated/gql'

const canteenMenuCartMenuDataFragment = graphql(`
  fragment CanteenMenuCart_MenuDataFragment on CCMealService {
    ...CanteenCart_MenuDataFragment
    ...CanteenCartTopUp_CurrentMenuDataFragment
    availableSubsidy
  }
`)

const canteenMenuCartUserDataFragment = graphql(`
  fragment CanteenMenuCart_UserDataFragment on User {
    ...CanteenCart_UserDataFragment
    ...CanteenCartTopUp_UserDataFragment
    subsidySettings {
      unlimitedSubsidy
    }
    wallet {
      balance
    }
  }
`)

export enum CartSection {
  Order = 1,
  TopUp = 2,
}

type Props = {
  open: boolean
  arrivalTime: string
  onClose: () => void
  onChangeTimeslot: (timeslot: string) => void
  onUpdate: (updatedItem: CanteenCartItem) => void
  onRemove: (itemReference: string) => void
  placeOrder: (bundle: number) => Promise<void>
  placingOrder: boolean
  cartItems: CanteenCartItem[]
  cartTotal: number
  currency: string
  userData: FragmentType<typeof canteenMenuCartUserDataFragment>
  menuData: FragmentType<typeof canteenMenuCartMenuDataFragment>
}

const MenuCart: React.FC<Props> = (props) => {
  const {
    currency,
    placeOrder,
    arrivalTime,
    cartItems,
    menuData: menuDataProp,
    userData: userDataProp,
    onClose,
    open,
    onUpdate,
    onChangeTimeslot,
    onRemove,
    cartTotal,
    placingOrder,
  } = props
  const menuData = useFragment(canteenMenuCartMenuDataFragment, menuDataProp)
  const userData = useFragment(canteenMenuCartUserDataFragment, userDataProp)

  const [currentSection, setCurrentSection] = useState<CartSection>(CartSection.Order)
  const [error, setError] = useState<string | null>(null)
  const [orderSucceeded] = useState(false)

  const handlePlaceOrder = (bundle: number) => {
    placeOrder(bundle)
  }

  const onCheckoutClicked = () => {
    const personalCredits = parseFloat(userData.wallet.balance.toString())
    const subtotal = cartTotal

    datadogRum.addAction('Checkout', {
      items: cartItems,
      subtotal,
    })

    if (
      !userData.subsidySettings.unlimitedSubsidy &&
      subtotal - menuData.availableSubsidy - personalCredits > 0
    ) {
      setCurrentSection(CartSection.TopUp)
    } else {
      handlePlaceOrder(0)
    }
  }

  const onTopUpCheckoutClicked = (bundle: number) => {
    handlePlaceOrder(bundle)
  }

  const onBackClicked = () => {
    setCurrentSection(CartSection.Order)
  }

  const onDismissError = () => {
    setError(null)
  }

  let content
  switch (currentSection) {
    case CartSection.TopUp:
      content = (
        <TopUpSection
          currency={currency}
          menuData={menuData}
          userData={userData}
          cartTotal={cartTotal}
          onBackClicked={onBackClicked}
          onCheckoutClicked={(bundle) => {
            onTopUpCheckoutClicked(bundle)
          }}
          loading={placingOrder}
        />
      )
      break
    default:
      content = (
        <Cart
          currency={currency}
          cartTotal={cartTotal}
          onRemove={onRemove}
          userData={userData}
          menuData={menuData}
          onChangeTimeslot={onChangeTimeslot}
          arrivalTime={arrivalTime}
          cartItems={cartItems}
          onClose={onClose}
          onUpdate={onUpdate}
          onCheckoutClicked={() => {
            onCheckoutClicked()
          }}
          onDismissError={onDismissError}
          loading={placingOrder}
          orderSucceeded={orderSucceeded}
          error={error}
        />
      )
      break
  }

  return (
    <Drawer
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: { sm: 430, xs: '100%' },
        },
      }}
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {content}
    </Drawer>
  )
}

export default MenuCart
